import { useMutation } from '@apollo/client';
import { AnalyticIdentifier, getCurrentTaxYear } from '@common/neo.tax';
import { ProgressProps } from '@ui-components/neo.tax';
import range from 'lodash/range';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CREDIT_CALCULATION_SERIALIZATION_KEY } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import { useGetDocuments, useQuestionSections } from '@neo.tax/rd/hooks';
import {
  useUpdateCompanyQuestion,
  FormSectionConfiguration,
  getProgress,
  UPDATE_RD_TAX_CREDIT_OVERRIDES,
  UpdateRdTaxCreditOverridesMutation,
  UpdateRdTaxCreditOverridesMutationVariables,
  FilingMethod,
  useAnalyticId,
  makeButtonGroupFormConfig,
  makeDropdownFormConfig,
  QuestionSectionCode,
  QuestionCode,
} from '@neo.tax/shared';

const UPDATE_CREDIT_FILING_METHOD = 'UPDATE_CREDIT_FILING_METHOD';

interface FilingMethodForms {
  filingMethodForms: FormSectionConfiguration;
  filingMethodProgress: ProgressProps;
  filingMethod: FilingMethod | undefined;
  actualBusinessStartIsoDate: string | null;
}

export const useFilingMethodForms = (): FilingMethodForms => {
  const filingMethodAnalyticId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_FILING_METHOD);
  const firstYearQreAndGrossReceiptsAnalyticId = useAnalyticId(
    AnalyticIdentifier.ENTERPRISE_FIRST_YEAR_QRE_AND_GROSS_RECEIPTS,
  );
  const { fiscalYear } = useRdContext();
  const { documents } = useGetDocuments({ fiscalYearId: fiscalYear.id });
  const { getAnswer, getQuestion, questionSections } = useQuestionSections();

  const currentIncorporationDate = getAnswer(
    questionSections,
    QuestionSectionCode.CompanyHistory,
    QuestionCode.CurrentIncorporationDate,
  );
  const originalIncorporationDate = getAnswer(
    questionSections,
    QuestionSectionCode.CompanyHistory,
    QuestionCode.OriginalIncorporationDate,
  );
  const actualBusinessStartIsoDate = originalIncorporationDate ?? currentIncorporationDate ?? null;
  const firstYearQreAndPyrQuestion = getQuestion(
    questionSections,
    QuestionSectionCode.CreditCalculation,
    QuestionCode.FirstYearQreAndPyr,
  );
  const firstYearQreAndPyrAnswer = getAnswer(
    questionSections,
    QuestionSectionCode.CreditCalculation,
    QuestionCode.FirstYearQreAndPyr,
  );
  const filingMethod = documents?.federalRdTaxCreditDocuments?.filingMethod;
  const isReducedCredit = documents?.federalRdTaxCreditDocuments?.isReducedCredit;
  const [year, setYear] = useState<number | undefined>(
    firstYearQreAndPyrAnswer ? parseInt(firstYearQreAndPyrAnswer) : undefined,
  );
  const updateFirstYearQreAndGrossReceiptsQuestion = useUpdateCompanyQuestion(
    firstYearQreAndPyrQuestion?.id ?? '',
    firstYearQreAndPyrQuestion?.questionId ?? '',
  );
  const [updateRdTaxCreditOverrides] = useMutation<
    UpdateRdTaxCreditOverridesMutation,
    UpdateRdTaxCreditOverridesMutationVariables
  >(UPDATE_RD_TAX_CREDIT_OVERRIDES);

  const currentTaxYear = fiscalYear ? getCurrentTaxYear(fiscalYear.taxYearEndDate) : new Date().getFullYear() - 1;

  // initialize the "year" state when existing answer is loaded
  useEffect(() => {
    if (firstYearQreAndPyrAnswer) {
      setYear(parseInt(firstYearQreAndPyrAnswer));
    }
  }, [firstYearQreAndPyrAnswer]);

  const handleFirstYearQreAndGrossReceipts = useCallback(
    (selectedYear: string): void => {
      setYear(parseInt(selectedYear));
      updateFirstYearQreAndGrossReceiptsQuestion({
        answerValues: selectedYear,
        questionCode: QuestionCode.FirstYearQreAndPyr,
      });
    },
    [updateFirstYearQreAndGrossReceiptsQuestion],
  );

  const filingMethodForms: FormSectionConfiguration = useMemo(
    () => ({
      title: 'Credit Calculation Method',
      subtitle:
        'Select the method you want to use to calculate your credit. Alternative Simplified Credit (ASC) is most common.',
      forms: [
        makeButtonGroupFormConfig({
          id: filingMethodAnalyticId,
          title: 'What method do you want to use to calculate your credit?',
          key: 'credit_calculation_method',
          value: filingMethod,
          options: [
            { value: FilingMethod.Asc, label: 'Alternative Simplified Credit method (ASC)' },
            { value: FilingMethod.Regular, label: 'Regular Method' },
          ],
          onChange: (newFilingMethod) => {
            const filingMethod = newFilingMethod as FilingMethod;
            updateRdTaxCreditOverrides({
              variables: {
                fiscalYearId: fiscalYear.id,
                rdTaxCreditOverrides: {
                  useFilingMethod: filingMethod,
                },
              },
              optimisticResponse: {
                updateRdTaxCreditOverrides: {
                  __typename: 'FederalRdTaxCreditDocuments',
                  id: fiscalYear.id,
                  filingMethod,
                  isReducedCredit: isReducedCredit ?? true,
                },
              },
              context: {
                debounceKey: UPDATE_CREDIT_FILING_METHOD,
                serializationKey: CREDIT_CALCULATION_SERIALIZATION_KEY,
              },
            });
          },
          subForms:
            filingMethod === FilingMethod.Regular && actualBusinessStartIsoDate
              ? [
                  makeDropdownFormConfig({
                    id: firstYearQreAndGrossReceiptsAnalyticId,
                    key: 'first_year_qre_and_gross_receipts',
                    title:
                      'What was the first year you had both Qualified Research Expenses (QRES) and Gross Receipts?',
                    value: year ?? '',
                    options: range(currentTaxYear, new Date(actualBusinessStartIsoDate).getFullYear() - 1, -1),
                    subtitle:
                      'QRE amounts must have been reported on prior year tax returns on Form 6765 line 9 or line 28. Gross receipts includes all forms of income: revenue from sales, government grants, awards, or interest from bank accounts (e.g., if you\'re a C-Corp, this is the amount on line 11 "Total Income" of Form 1120).',
                    onChange: (year: string | number) => handleFirstYearQreAndGrossReceipts(`${year}`),
                  }),
                ]
              : [],
        }),
      ],
    }),
    [
      actualBusinessStartIsoDate,
      currentTaxYear,
      filingMethod,
      filingMethodAnalyticId,
      firstYearQreAndGrossReceiptsAnalyticId,
      handleFirstYearQreAndGrossReceipts,
      fiscalYear.id,
      isReducedCredit,
      updateRdTaxCreditOverrides,
      year,
    ],
  );

  const filingMethodProgress = useMemo(
    () => (filingMethodForms ? getProgress(filingMethodForms.forms) : { total: 0, completed: 0 }),
    [filingMethodForms],
  );

  return {
    filingMethod,
    filingMethodForms,
    filingMethodProgress,
    actualBusinessStartIsoDate,
  };
};
